import React, { RefObject, useEffect, useRef, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $insertNodes } from "lexical";
import {$createImageNode} from '../nodes/ImageNode';
import PlusIcon from "../../../icons/plus.icon";
import FileUpIcon from "../../../icons/fileUp.icon";
import { MediaListFullPane } from "../../media/mediaSelector.comp";
import { MediaFilter } from "../../../types/media";
import { useForm } from "react-hook-form";
import { getMediaListByIds, uploadMedia } from "../../../core/media.core";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from '../../../core/translation.core';
import ImageIcon from "../../../icons/richicons/imageIcon";
import { toast } from "react-toastify";
import { useEditorMeta } from "../hooks/useMeta";
import { useAutoSaveDraft } from "../hooks/useSaveDraft";


export default function ImageToolBar() {
  const [editor] = useLexicalComposerContext();

  const { productId, locale } = useEditorMeta();
  const scheduleSave = useAutoSaveDraft(editor, productId, locale);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [ids, setIds] = useState<string[]>([]);
  const [isOpened, setIsOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const methods = useForm<MediaFilter>({
    defaultValues: {
      page: 3,
    },
  });

  const handleFileChange = async (e) => {
    setLoading(true);
    const file  = e.target.files;
    if (!file) return;

     try {
      const mediaData = await uploadMedia(file, queryClient);
      const ids = mediaData.map((m) => m.media.id);
      setIds((prev) => [...prev, ...ids])

      queryClient.invalidateQueries({ queryKey: ["mediaList"] });

      await showMedia(ids)
      setLoading(false);
      clickCancelled();
    } catch (error) {
      toast.error(error.message)
      setLoading(false);
    }
  };

  function toggleSelected(mediaId: string, isSelected: boolean) {
    setIdAndFireChanged((items) => {
      if (isSelected) {
        return [...items, mediaId];
      } else {
        return items.filter((id) => mediaId !== id);
      }
      // empty the ids since this indepdent of te nodes
    });
  }

  function setIdAndFireChanged(newValueCallback: (items: string[]) => string[]) {
    setIds((prevItems) => {
      const newValue = newValueCallback(prevItems);
      return newValue;
    });
  }

  async function addSelected(ids: string[]) {
    setIsOpened(false);
    dialogRef.current?.showModal();
    setLoading(true);
    await showMedia(ids);
    setIds([]);
    setLoading(false);
    clickCancelled();
  }

  const clickCancelled = () => {
    dialogRef.current?.close();
  };

  const clickAddImage = () => {
    dialogRef.current?.showModal();
  };

  const showMedia = async (ids: string[]) => {
    try {
      const medialist = await getMediaListByIds(ids);

      editor.update(() => {
        medialist.map((m) => {
          const imageNode = $createImageNode({src: m.url, altText: m.filename, width: m.width, height: m.height, locale: locale, productId: productId})
          console.log(imageNode)
          $insertNodes([imageNode]);
        });
      })

      scheduleSave();
    } catch (error) {
      console.log(error);
      toast.error(error.message)
    }
  }


  return (
  <>
    <div className="">
      <button
        type="button"
        onClick={clickAddImage}
        className=" text-sm dark:bg-gray-800 bg-slate-100 rounded-full"
      >
        <ImageIcon className="w-5 h-5" />
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFileChange}
        multiple
        name="media"
      />
    </div>
    <ImageUploadDialog setIsOpened={setIsOpened} fileInputRef={fileInputRef} dialogRef={dialogRef} clickCancelled={clickCancelled} isLoading={loading} />
    <MediaListFullPane toggleSelected={toggleSelected} ids={ids} methods={methods} setIsOpened={setIsOpened} isOpened={isOpened}  addSelected={() => addSelected(ids)} />
  </>
  );
}

function ImageUploadDialog({ dialogRef, clickCancelled, fileInputRef, setIsOpened, isLoading} : {dialogRef: RefObject<HTMLDialogElement>; clickCancelled: () => void; fileInputRef?: RefObject<HTMLInputElement>; setIsOpened: (isOpened: boolean) => void; isLoading: boolean}) {
  const { t, msg } = useTranslation();
 
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || isLoading) return;

    const handleOutsideClick = (e: MouseEvent) => {
      const rect = dialog.getBoundingClientRect();
      const inside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;

      if (!inside) {
        dialog.close();
        clickCancelled();
      }
    };

    dialog.addEventListener("click", handleOutsideClick);
    return () => dialog.removeEventListener("click", handleOutsideClick);
  }, [dialogRef, clickCancelled, isLoading]);

  return (
    <dialog className="p-4 dark:bg-her-backblue dark:text-white rounded-lg min-w-[250px]" ref={dialogRef}>
      {isLoading ? 
        <div className="h-10 flex items-center space-x-2">
          <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-black dark:border-white"></span>
          <span className="text-[12px]">{t(msg.product.pleaseWait)}</span>
        </div> :
        <div className="flex flex-col gap-2 ">
          <button disabled={isLoading} onClick={() => fileInputRef?.current?.click()} type="button" className="dark:hover:bg-gray-800 hover:bg-slate-100 flex flex-row gap-2 items-center p-2 rounded-lg">
            <PlusIcon className="w-4 h-4" />
            <span className="text-[12px]" >{t(msg.product.uploadNew)}</span>
          </button>
          <button disabled={isLoading} onClick={() => {
            dialogRef.current?.close();
            setIsOpened(true)
          }} type="button" className="dark:hover:bg-gray-800 hover:bg-slate-100 flex flex-row gap-2 items-center p-2 rounded-lg">
            <FileUpIcon className="w-4 h-4"/>
            <span className="text-[12px]">{t(msg.product.chooseRecent)}</span>
          </button>
        </div>
      }
    </dialog>
  )
}
